import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { SpinnerGap, Plus, CalendarCheck, Check, Copy } from "@phosphor-icons/react";

function formatMonth(m) {
  if (!m) return "—";
  try { const d = new Date(m + "-01"); return d.toLocaleDateString("en-US", { month: "long", year: "numeric" }); }
  catch { return m; }
}

export default function PlansListPage() {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const businessId = user?.active_business_id || "";
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [duplicatingId, setDuplicatingId] = useState(null);

  useEffect(() => {
    const f = async () => {
      try { const r = await api.get(`/plans?business_id=${businessId}`); setPlans(r.data.plans || []); } catch { /* non-critical */ }
      setLoading(false);
    };
    f();
  }, [api, businessId]);

  const handleDuplicate = async (e, planId) => {
    e.stopPropagation();
    setDuplicatingId(planId);
    try {
      const resp = await api.post(`/plans/${planId}/duplicate`, {});
      setPlans(prev => [resp.data, ...prev]);
      toast.success("Plan duplicated!");
    } catch { toast.error("Failed to duplicate"); }
    setDuplicatingId(null);
  };

  return (
    <div className="min-h-screen bg-[var(--bg-base)]">
      <Header />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-[var(--text-primary)] font-['Cormorant_Garamond']" data-testid="plans-list-title">Content Plans</h1>
            <p className="text-sm text-[var(--text-muted)] mt-1">Monthly content strategies powered by AI</p>
          </div>
          <Button onClick={() => navigate("/plans/new")} className="bg-[#FF4400] hover:bg-[#FF6633] text-white" data-testid="new-plan-btn">
            <Plus size={16} className="mr-1.5" /> New Plan
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><SpinnerGap size={28} className="animate-spin text-[#FF4400]" /></div>
        ) : plans.length === 0 ? (
          <div className="text-center py-20" data-testid="plans-empty-state">
            <CalendarCheck size={48} className="mx-auto text-[var(--text-muted)] opacity-30 mb-4" weight="duotone" />
            <h2 className="text-lg font-semibold text-[var(--text-primary)] mb-2">No content plans yet</h2>
            <p className="text-sm text-[var(--text-muted)] mb-6 max-w-md mx-auto">Create a monthly plan to get AI-recommended content mix aligned with your business goals.</p>
            <Button onClick={() => navigate("/plans/new")} className="bg-[#FF4400] hover:bg-[#FF6633] text-white"><Plus size={16} className="mr-1.5" /> Create Your First Plan</Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="plans-grid">
            {plans.map((plan, idx) => {
              const items = plan.planned_items || [];
              const done = items.filter(i => i.status === "generated" || i.status === "published").length;
              const pct = items.length > 0 ? Math.round((done / items.length) * 100) : 0;
              return (
                <motion.div key={plan.plan_id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.04 }}
                  onClick={() => navigate(`/plans/${plan.plan_id}`)}
                  className="bg-[var(--bg-card)] border border-[var(--border)] rounded-lg p-5 hover:border-[var(--border-strong)] transition-colors cursor-pointer group" data-testid={`plan-card-${idx}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="text-xs text-[var(--text-muted)] uppercase tracking-wider font-medium">{formatMonth(plan.plan_month)}</p>
                      <h3 className="text-sm font-semibold text-[var(--text-primary)] mt-0.5">{plan.title}</h3>
                    </div>
                    <span className={`px-2 py-0.5 text-[9px] font-medium rounded-full ${plan.status === "active" ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20" : plan.status === "completed" ? "bg-blue-500/10 text-blue-400 border border-blue-500/20" : "bg-amber-500/10 text-amber-400 border border-amber-500/20"}`}>
                      {plan.status}
                    </span>
                  </div>
                  <p className="text-xs text-[var(--text-muted)] mb-3">{items.length} items · {done} generated</p>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1.5 rounded-full bg-[var(--bg-base)] overflow-hidden">
                      <div className="h-full rounded-full bg-[#FF4400] transition-all" style={{ width: `${pct}%` }} />
                    </div>
                    <button onClick={(e) => handleDuplicate(e, plan.plan_id)} disabled={duplicatingId === plan.plan_id} className="p-1.5 rounded-md text-[var(--text-muted)] hover:text-[#FF4400] hover:bg-[#FF4400]/10 transition-colors opacity-0 group-hover:opacity-100" title="Duplicate plan" data-testid={`duplicate-plan-${idx}`}>
                      {duplicatingId === plan.plan_id ? <SpinnerGap size={14} className="animate-spin" /> : <Copy size={14} />}
                    </button>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
